import React from "react";
import { styled } from "@mui/material/styles";
import Link from "next/link";
import Head from "next/head";
import funnel from "../styles/Funnel.module.css";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import Footer from "../component/footer";

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const SelectButton = styled(Link)(({ theme }) => ({
  color: "#fff",
  border: "1px solid var(--primary-color)",
  fontWeight: "600",
  width: "100%",
  height: "45px",
  fontSize: "16px",
  backgroundColor: "var(--primary-color)",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  marginTop: "15px",
  "&:hover": {
    border: "1px solid var(--primary-color)",
    backgroundColor: "var(--dark-color)",
  },
}));

export default function StateDirectory() {
  return (
    <>
      <Head>
        <title>
          Find the Best Suburbs for Property Investment in Australia
        </title>
        <link
          rel="canonical"
          href="https://abodefinder.com.au/state-directory/"
        ></link>
        <meta
          name="description"
          content="Find the best suburbs for property investment in Australia with AbodeFinder. Our comprehensive suburbs profile provides valuable insights into the property market, including growth rates, yields, and rental returns. Discover the most promising suburbs to invest in with our informative demographics."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" sizes="32x32" href="https://abodefinder.com.au/blog-static/img/favicon-32.ico"/>
      </Head>
      <div className={funnel.allFunnel}>
        <div className={funnel.funnelChance}>
          <div className={funnel.funnelBody} style={{ marginTop: "40px" }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <div className={funnel.boxQn}>
                <h1 className={funnel.h1Text}>
                  Comprehensive Suburbs Profile and Investment Insights for
                  Australian Property Market
                </h1>
                <h2
                  className={funnel.h2TextFunnel}
                  style={{ marginBottom: "10px" }}
                >
                  Discover the Most Promising Suburbs to Invest in with
                  AbodeFinder
                </h2>
                <p className={funnel.pText}>
                  AbodeFinder offers a comprehensive directory of Australian
                  suburbs with informative demographic data and key property
                  market trends. Use our easy-to-read demographics to make
                  informed investment decisions and find the most promising
                  suburbs for your investment strategy.
                </p>

                <div className={funnel.directoryTitleList}>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/act-suburbs-directory/" target="_blank">
                      Australian Capital Territory
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/nsw-suburbs-directory/" target="_blank">
                      New South Wales
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/nt-suburbs-directory/" target="_blank">
                      Northern Territory
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/qld-suburbs-directory/" target="_blank">
                      Queensland
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/sa-suburbs-directory/" target="_blank">
                      South Australia
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/tas-suburbs-directory/" target="_blank">
                      Tasmania
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/vic-suburbs-directory/" target="_blank">
                      Victoria
                    </Link>
                  </div>
                  <div className={funnel.directoryTitleTextBox}>
                    <Link href="/wa-suburbs-directory/" target="_blank">
                      Western Australia
                    </Link>
                  </div>
                </div>
              </div>
            </Box>
          </div>
        </div>
      </div>
      <Container sx={{ flexGrow: 12, mt: 3, mb: 10 }}>
        <Item
          variant="outlined"
          sx={(theme) => ({
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "center",
            p: 2,
            mb: 5,
            mt: 5,
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              width: "100%",
            },
          })}
        >
          <Item
            variant="outlined"
            sx={(theme) => ({
              width: "48%",
              height: "310px",
              mr: 2,
              mt: 1,
              mb: 1,
              [theme.breakpoints.down("md")]: { width: "100%", height: "auto" },
            })}
          >
            <CardContent
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: "var(--primary-color)",
                }}
              >
                <WhereToVoteIcon sx={{ fontSize: "50px" }} />
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", mt: 3 }}
              >
                Find the perfect suburb for your next property investment with
                SuburbFinder. Get customised recommendations based on your
                budget, public transport needs, and school preferences.
              </Typography>
              <SelectButton href="/suburb-finder/">
                Find Your Ideal Suburb Now
              </SelectButton>
            </CardContent>
          </Item>
          <Item
            variant="outlined"
            sx={(theme) => ({
              width: "48%",
              height: "310px",
              mr: 2,
              mt: 1,
              mb: 1,
              [theme.breakpoints.down("md")]: { width: "100%", height: "auto" },
            })}
          >
            <CardContent
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: "var(--primary-color)",
                }}
              >
                <QueryStatsIcon sx={{ fontSize: "50px" }} />
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", mt: 3 }}
              >
                Calculate your chances of buying the perfect property with our
                AI-powered Buying Chance Calculator. Get personalized insights
                based on your budget and selected suburbs.
              </Typography>
              <SelectButton href="/buying-chance/">
                Get Personalised Buying Insights Now
              </SelectButton>
            </CardContent>
          </Item>
        </Item>
      </Container>
      <Footer />
    </>
  );
}
